import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Level-1 auth stack: access token is held in memory only. The long-lived
// refresh token lives in an HttpOnly cookie the browser sends automatically;
// the SPA reflects the (readable) sf_csrf cookie on state-changing calls.
let _accessToken = null;
let _refreshInFlight = null;

export function getToken() { return _accessToken; }
export function saveToken(t) { _accessToken = t || null; }
export function clearToken() { _accessToken = null; }

function _cookie(name) {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
}

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
    if (_accessToken) config.headers.Authorization = `Bearer ${_accessToken}`;
    const method = (config.method || "get").toLowerCase();
    if (method !== "get" && method !== "head") {
        const csrf = _cookie("sf_csrf");
        if (csrf) config.headers["X-CSRF-Token"] = csrf;
    }
    return config;
});

// Auto-refresh on 401.
//
// OPS-004B3F · Both the AuthContext bootstrap AND the interceptor
// share the SAME `_refreshInFlight` promise via `refreshAccessToken()`
// so a bootstrap-initiated refresh can never race a losing
// interceptor-triggered refresh whose 401 (caused by token rotation
// from the winning call) would otherwise clear the just-established
// session. A late 401 from a stale/superseded refresh call is now
// downgraded to a no-op when the in-memory access token has already
// been refreshed by another caller.
export function refreshAccessToken() {
    if (!_refreshInFlight) {
        _refreshInFlight = client.post("/auth/refresh")
            .then((r) => {
                if (r?.data?.access_token) {
                    _accessToken = r.data.access_token;
                }
                return r?.data || null;
            })
            .finally(() => { _refreshInFlight = null; });
    }
    return _refreshInFlight;
}

client.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const isAuthCall = (original.url || "").includes("/auth/");
        if (status === 401 && !original._retried && !isAuthCall) {
            original._retried = true;
            // Capture the token that was current when this request
            // failed; if another concurrent refresh has since
            // rotated the in-memory token we simply retry with the
            // new one and never touch `_accessToken` on failure.
            const tokenAtFailure = _accessToken;
            try {
                const data = await refreshAccessToken();
                if (data?.access_token || _accessToken) {
                    original.headers = {
                        ...(original.headers || {}),
                        Authorization: `Bearer ${_accessToken}`,
                    };
                    return client(original);
                }
            } catch (_) {
                // A losing refresh (superseded by a concurrent
                // successful rotation) MUST NOT sign out a session
                // that another caller has just established.
                if (_accessToken && _accessToken !== tokenAtFailure) {
                    original.headers = {
                        ...(original.headers || {}),
                        Authorization: `Bearer ${_accessToken}`,
                    };
                    return client(original);
                }
                clearToken();
            }
        }
        return Promise.reject(error);
    },
);

/**
 * Mirrors /frontend/src/api.ts on the mobile app so backend/frontend contracts
 * stay in sync. Response shapes are identical.
 */
export const api = {
    login: (email, password) => client.post("/auth/login", { email, password }).then(r => r.data),
    register: (email, password, name, role) => client.post("/auth/register", { email, password, name, role }).then(r => r.data),
    me: () => client.get("/auth/me").then(r => r.data),
    deleteAccount: () => client.delete("/auth/me").then(r => r.data),
    // OPS-004B3F · Route through the shared `_refreshInFlight` in the
    // interceptor so the bootstrap call and any concurrent 401-driven
    // refresh share ONE server rotation and never race each other.
    refreshToken: () => refreshAccessToken(),
    logout: () => client.post("/auth/logout").then(r => r.data),
    logoutAll: () => client.post("/auth/logout-all").then(r => r.data),
    logoutOthers: () => client.post("/auth/logout-others").then(r => r.data),
    forgotPassword: (email) => client.post("/auth/forgot-password", { email }).then(r => r.data),
    resetPassword: (token, new_password, confirm_password) =>
        client.post("/auth/reset-password", { token, new_password, confirm_password }).then(r => r.data),
    changePassword: (current_password, new_password, confirm_password) =>
        client.post("/auth/change-password", { current_password, new_password, confirm_password }).then(r => r.data),
    // DEV-015D · Genuine-activity ping. Backend debounces; caller-side
    // logic in `useIdleTimeout` also debounces so we only invoke this
    // when a real user interaction has fired.
    reportActivity: () => client.post("/auth/activity").then(r => r.data),

    // Security admin
    adminListUsers: (q = "") => client.get(`/admin/security/users${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(r => r.data),
    adminGetUser: (id) => client.get(`/admin/security/users/${id}`).then(r => r.data),
    adminUserAudit: (id, limit = 100) => client.get(`/admin/security/users/${id}/audit?limit=${limit}`).then(r => r.data),
    adminUnlock: (id) => client.post(`/admin/security/users/${id}/unlock`, { confirm: true }).then(r => r.data),
    adminSuspend: (id) => client.post(`/admin/security/users/${id}/suspend`, { confirm: true }).then(r => r.data),
    adminReactivate: (id) => client.post(`/admin/security/users/${id}/reactivate`, { confirm: true }).then(r => r.data),
    adminDisable: (id) => client.post(`/admin/security/users/${id}/disable`, { confirm: true }).then(r => r.data),
    adminEnable: (id) => client.post(`/admin/security/users/${id}/enable`, { confirm: true }).then(r => r.data),
    adminResetPassword: (id) => client.post(`/admin/security/users/${id}/reset-password`, { confirm: true }).then(r => r.data),
    adminRevokeSessions: (id) => client.post(`/admin/security/users/${id}/revoke-sessions`, { confirm: true }).then(r => r.data),
    adminForcePasswordChange: (id) => client.post(`/admin/security/users/${id}/force-password-change`, { confirm: true }).then(r => r.data),
    adminResetToDemoPassword: (id) => client.post(`/admin/security/users/${id}/reset-to-demo-password`, { confirm: true }).then(r => r.data),

    // OPS-CUST-001 · Operations Customer workspace (read-only)
    opsListCustomers: ({ q = "", filter = "active" } = {}) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (filter) params.set("filter", filter);
        const qs = params.toString();
        return client.get(`/operations/customers${qs ? `?${qs}` : ""}`).then(r => r.data);
    },
    opsGetCustomer: (id) => client.get(`/operations/customers/${id}`).then(r => r.data),

    // OPS-DOC-001 · Operations Document Centre (read-only)
    opsListDocuments: ({ q = "", filter = "active", category = "", reference_health = "" } = {}) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (filter) params.set("filter", filter);
        if (category) params.set("category", category);
        if (reference_health) params.set("reference_health", reference_health);
        const qs = params.toString();
        return client.get(`/operations/documents${qs ? `?${qs}` : ""}`).then(r => r.data);
    },
    opsGetDocument: (id) => client.get(`/operations/documents/${id}`).then(r => r.data),

    // DEV-025 (extension) · Relink / Archive / Restore for orphaned
    // documents. Every write goes through Operations → Documents
    // and re-validates permissions + associations on the server.
    opsSearchProjectsForRelink: (q, limit = 20) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (limit) params.set("limit", String(limit));
        const qs = params.toString();
        return client.get(`/operations/documents/projects/search${qs ? `?${qs}` : ""}`).then(r => r.data);
    },
    opsRelinkDocument: (id, { project_id, expected_project_id }) =>
        client.patch(`/operations/documents/${id}/relink`, {
            project_id,
            ...(expected_project_id !== undefined ? { expected_project_id } : {}),
        }).then(r => r.data),
    opsArchiveDocument: (id, reason) =>
        client.post(`/operations/documents/${id}/archive`, { reason }).then(r => r.data),
    opsRestoreDocument: (id) =>
        client.post(`/operations/documents/${id}/restore`).then(r => r.data),

    // DEV-011C · Customer invite lifecycle (Operations)
    opsListInvitations: (customerId) =>
        client.get(`/operations/customers/${customerId}/invitations`).then(r => r.data),
    opsSendInvitation: (customerId, body) =>
        client.post(`/operations/customers/${customerId}/invitations`, body).then(r => r.data),
    opsResendInvitation: (customerId, body) =>
        client.post(`/operations/customers/${customerId}/invitations/resend`, body).then(r => r.data),
    opsRevokeInvitation: (customerId, invitationId) =>
        client.delete(`/operations/customers/${customerId}/invitations/${invitationId}`).then(r => r.data),
    // DEV-011C · Public activation
    verifyCustomerInvitation: (token) =>
        client.get(`/auth/customer/invitation/${encodeURIComponent(token)}`).then(r => r.data),
    activateCustomerAccount: (body) =>
        client.post("/auth/customer/activate", body).then(r => r.data),

    // DEV-002 — Unified Activity Engine
    listActivity: (params = {}) => {
        // FastAPI parses list query params as `?key=a&key=b` (repeat format),
        // but axios' default serialiser uses `?key[]=a&key[]=b`. Explicitly
        // serialise arrays as repeats so `type` and `project_ids` reach the
        // backend correctly.
        const serialize = (obj) => {
            const parts = [];
            for (const [k, v] of Object.entries(obj)) {
                if (v === undefined || v === null) continue;
                if (Array.isArray(v)) {
                    for (const item of v) parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(item)}`);
                } else {
                    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
                }
            }
            return parts.join("&");
        };
        const qs = serialize(params);
        return client.get(`/activity${qs ? `?${qs}` : ""}`).then((r) => r.data);
    },
    adminInitiatePasswordReset: (id) => client.post(`/admin/security/users/${id}/initiate-password-reset`, { confirm: true }).then(r => r.data),
    adminGetPolicy: () => client.get("/admin/security/policy").then(r => r.data),
    adminPatchPolicy: (body) => client.patch("/admin/security/policy", body).then(r => r.data),

    // Admin bulk delete (installer-admin only). `ctx` may carry a
    // `bulk_operation_id` when the caller is running a multi-batch delete
    // so audit entries share a common trace id.
    bulkDeleteUsers:        (ids, ctx = {}) => client.post("/admin/users/bulk-delete",        { ids, ...ctx }).then(r => r.data),
    bulkDeleteUsersPreview: (ids) => client.post("/admin/users/bulk-delete-preview", { ids }).then(r => r.data),
    repairSoftDeletedUsers: () => client.post("/admin/users/repair-soft-deletes").then(r => r.data),

    // Security Administration console
    adminCreateUser:  (body) => client.post("/admin/security/users", body).then(r => r.data),
    adminPatchUser:   (id, body) => client.patch(`/admin/security/users/${id}`, body).then(r => r.data),
    adminGrantSecurityAdmin:  (id) => client.post(`/admin/security/users/${id}/grant-security-admin`, { confirm: true }).then(r => r.data),
    adminRevokeSecurityAdmin: (id) => client.post(`/admin/security/users/${id}/revoke-security-admin`, { confirm: true }).then(r => r.data),
    adminListUserSessions: (id) => client.get(`/admin/security/users/${id}/sessions`).then(r => r.data),
    adminRevokeUserSession: (id, sid) => client.post(`/admin/security/users/${id}/sessions/${sid}/revoke`).then(r => r.data),
    adminRevokeOtherUserSessions: (id) => client.post(`/admin/security/users/${id}/sessions/revoke-others`, { confirm: true }).then(r => r.data),
    adminQueryAudit: (params = {}) => client.get("/admin/security/audit", { params }).then(r => r.data),
    bulkDeleteProjects:     (ids, ctx = {}) => client.post("/admin/projects/bulk-delete",     { ids, ...ctx }).then(r => r.data),
    bulkDeleteTasks:        (ids, ctx = {}) => client.post("/admin/tasks/bulk-delete",        { ids, ...ctx }).then(r => r.data),
    bulkDeleteAppointments: (ids, ctx = {}) => client.post("/admin/appointments/bulk-delete", { ids, ...ctx }).then(r => r.data),

    // Permanent test-data purge (dev/test envs OR is_test_data users)
    adminPurgePreview: (ids) => client.post("/admin/users/purge-preview", { ids }).then(r => r.data),
    adminPermanentPurge: (ids, confirmation, ctx = {}) =>
        client.post("/admin/users/permanent-purge", { ids, confirmation, ...ctx }).then(r => r.data),
    adminWipeDemoDataPreview: () => client.get("/admin/users/wipe-demo-data/preview").then(r => r.data),
    adminWipeDemoData: (confirmation) => client.post("/admin/users/wipe-demo-data", { confirmation }).then(r => r.data),

    // ADMIN-DATA-ORPHAN-CLEANUP · Read-only orphan-exception audit + controlled
    // resolve. The dry-run returns a deterministic `content_hash` the resolve
    // request must echo back — a stale hash refuses the entire batch.
    adminOrphanExceptionsDryRun: () =>
        client.post("/admin/data/orphan-exceptions/dry-run").then(r => r.data),
    adminOrphanExceptionsResolve: ({ exception_ids, content_hash, confirmation = "RESOLVE" }) =>
        client.post("/admin/data/orphan-exceptions/resolve", {
            exception_ids, content_hash, confirmation,
        }).then(r => r.data),

    // OPS-008A · Read-only document-reference audit. Zero writes.
    // No `/resolve` twin in v1 — the audit only reports.
    adminDocumentReferencesDryRun: () =>
        client.post("/admin/data/document-references/dry-run").then(r => r.data),

    // DEV-025 · Orphaned zero-byte document cleanup. Strict predicate:
    // no file bytes AND no valid project AND no valid customer link.
    // `apply` requires the `content_hash` from a recent dry-run + the
    // literal "REMOVE" confirmation.
    adminDocumentOrphansDryRun: () =>
        client.post("/admin/data/document-orphans/dry-run").then(r => r.data),
    adminDocumentOrphansApply: ({ document_ids, content_hash, confirmation = "REMOVE" }) =>
        client.post("/admin/data/document-orphans/apply", {
            document_ids, content_hash, confirmation,
        }).then(r => r.data),

    myProject: () => client.get("/projects/mine").then(r => r.data),
    listProjects: () => client.get("/projects").then(r => r.data),
    listProjectsSummary: () => client.get("/projects/summary").then(r => r.data),
    updateProjectState: (id, state) => client.patch(`/projects/${id}/state`, { state }).then(r => r.data),
    getProject: (id) => client.get(`/projects/${id}`).then(r => r.data),
    createProject: (body) => client.post("/projects", body).then(r => r.data),
    // DEV-018 · Sales attribution helpers.
    listSalesSources: () => client.get("/projects/sales-sources").then(r => r.data),
    listSalesOwners: (q) => client.get("/projects/sales-owners", { params: q ? { q } : {} }).then(r => r.data),
    updateProjectAttribution: (id, body) =>
        client.patch(`/projects/${id}/attribution`, body).then(r => r.data),
    // DEV-019 · Sales workflow — action catalogue + lifecycle + outcome.
    listSalesActionTypes: () => client.get("/projects/sales-action-types").then(r => r.data),
    listLostReasons: () => client.get("/projects/lost-reasons").then(r => r.data),
    createSalesAction: (id, body) =>
        client.post(`/projects/${id}/sales-actions`, body).then(r => r.data),
    updateSalesAction: (id, actionId, body) =>
        client.patch(`/projects/${id}/sales-actions/${actionId}`, body).then(r => r.data),
    completeSalesAction: (id, actionId) =>
        client.post(`/projects/${id}/sales-actions/${actionId}/complete`).then(r => r.data),
    // DEV-019G · Sales To-do workspace data source. Thin read over
    // the existing ``projects.sales_actions[]`` sub-array scoped to
    // the caller's visible-project set — no new backend collection.
    listSalesActions: (filter = "outstanding") =>
        client.get("/sales/actions", { params: { filter } }).then(r => r.data),
    updateOpportunityOutcome: (id, body) =>
        client.patch(`/projects/${id}/opportunity`, body).then(r => r.data),
    // DEV-019 UAT3 · Sales lifecycle stage
    listSalesLifecycleStages: () => client.get("/projects/sales-lifecycle-stages").then(r => r.data),
    updateSalesStage: (id, sales_stage) =>
        client.patch(`/projects/${id}/sales-stage`, { sales_stage }).then(r => r.data),
    updatePhaseTaskStatus: (projectId, taskId, status, opts = {}) =>
        client.patch(`/projects/${projectId}/tasks/${taskId}`,
            { status, ...(opts.overrideReason ? { override_reason: opts.overrideReason } : {}) }
        ).then(r => r.data),

    listMessages: (projectId) => client.get(`/projects/${projectId}/messages`).then(r => r.data),
    sendMessage: (projectId, text) => client.post(`/projects/${projectId}/messages`, { text }).then(r => r.data),
    markProjectMessagesRead: (projectId) => client.post(`/projects/${projectId}/messages/read`).then(r => r.data),
    // CUST-EXP-001A · Read-only role-scoped unread count for the
    // Command Centre "What you need to do" card. Never mutates.
    projectMessagesUnreadCount: (projectId) =>
        client.get(`/projects/${projectId}/messages/unread-count`).then(r => r.data),
    listEquipment: (projectId) => client.get(`/projects/${projectId}/equipment`).then(r => r.data),

    // SYS-001 · Canonical "My System" equipment mutations (Operations-only)
    opsCreateEquipment: (projectId, body) =>
        client.post(`/projects/${projectId}/equipment`, body).then(r => r.data),
    opsUpdateEquipment: (equipmentId, body) =>
        client.patch(`/equipment/${equipmentId}`, body).then(r => r.data),
    opsArchiveEquipment: (equipmentId) =>
        client.delete(`/equipment/${equipmentId}`).then(r => r.data),

    listDocuments: (projectId) => client.get(`/projects/${projectId}/documents`).then(r => r.data),
    uploadDocument: (projectId, file, title, category, visibility, internalNote) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title || file.name);
        fd.append("category", category || "Customer Upload");
        if (visibility) fd.append("visibility", visibility);
        if (internalNote) fd.append("internal_note", internalNote);
        return client.post(`/projects/${projectId}/documents/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        }).then(r => r.data);
    },
    updateDocument: (docId, body) => client.patch(`/documents/${docId}`, body).then(r => r.data),
    deleteDocument: (docId) => client.delete(`/documents/${docId}`).then(r => r.data),
    documentDownloadUrl: (docId) => `${API}/documents/${docId}/download`,
    documentDownloadToken: (docId) => client.get(`/documents/${docId}/download-token`).then(r => r.data),
    handoverPdfUrl: (projectId) => `${API}/projects/${projectId}/handover.pdf`,
    handoverDownloadToken: (projectId) => client.get(`/projects/${projectId}/handover-download-token`).then(r => r.data),

    listAppointments: (projectId) => client.get(`/projects/${projectId}/appointments`).then(r => r.data),
    listAppointmentsAll: (projectId) => client.get(`/projects/${projectId}/appointments/all`).then(r => r.data),
    createAppointment: (projectId, body) => client.post(`/projects/${projectId}/appointments`, body).then(r => r.data),
    updateAppointment: (id, body) => client.patch(`/appointments/${id}`, body).then(r => r.data),

    // DEV-008 · Delivery & site logistics (Operations-only).
    listLogistics: (projectId) => client.get(`/projects/${projectId}/logistics`).then(r => r.data),
    createLogistics: (projectId, body) => client.post(`/projects/${projectId}/logistics`, body).then(r => r.data),
    updateLogistics: (id, body) => client.patch(`/logistics/${id}`, body).then(r => r.data),
    deleteLogistics: (id) => client.delete(`/logistics/${id}`).then(r => r.data),

    // DEV-010A · Digital Site Survey (Operations + assigned Engineer).
    getProjectSurvey: (projectId) => client.get(`/projects/${projectId}/survey`).then(r => r.data),
    createOrOpenSurvey: (projectId, appointmentId = null) =>
        client.post(`/projects/${projectId}/survey`, null, {
            params: appointmentId ? { appointment_id: appointmentId } : {},
        }).then(r => r.data),
    updateSurvey: (surveyId, body) => client.patch(`/surveys/${surveyId}`, body).then(r => r.data),
    createElevation: (surveyId, body) => client.post(`/surveys/${surveyId}/elevations`, body).then(r => r.data),
    updateElevation: (id, body) => client.patch(`/elevations/${id}`, body).then(r => r.data),
    deleteElevation: (id, opts) => client.delete(`/elevations/${id}`,
        opts?.reapprove ? { params: { reapprove: true } } : undefined).then(r => r.data),

    // DEV-010B / DEV-010B-2 · Survey submission + photo attachment.
    submitSurvey: (surveyId, body) => client.post(`/surveys/${surveyId}/submit`, body).then(r => r.data),
    listSurveyPhotos: (surveyId) => client.get(`/surveys/${surveyId}/photos`).then(r => r.data),
    attachSurveyPhoto: (surveyId, body) => client.post(`/surveys/${surveyId}/photos`, body).then(r => r.data),
    detachSurveyPhoto: (documentId) => client.delete(`/survey-photos/${documentId}`).then(r => r.data),

    // DEV-010H · Canonical completion state + Ops edit audit trail.
    getSurveyCompletion: (surveyId) => client.get(`/surveys/${surveyId}/completion`).then(r => r.data),
    getSurveyAudit: (surveyId) => client.get(`/surveys/${surveyId}/audit`).then(r => r.data),

    // DEV-010C · Operations review, variance decisions & approval.
    getSurveyReview: (surveyId) => client.get(`/surveys/${surveyId}/review`).then(r => r.data),
    decideVariance: (surveyId, body) => client.post(`/surveys/${surveyId}/variance-decisions`, body).then(r => r.data),
    createReviewItem: (surveyId, body) => client.post(`/surveys/${surveyId}/review-items`, body).then(r => r.data),
    decideReviewItem: (surveyId, itemId, body) => client.patch(`/surveys/${surveyId}/review-items/${itemId}`, body).then(r => r.data),
    returnSurvey: (surveyId, body) => client.post(`/surveys/${surveyId}/return`, body).then(r => r.data),
    resubmitSurvey: (surveyId, body) => client.post(`/surveys/${surveyId}/resubmit`, body).then(r => r.data),
    approveSurvey: (surveyId, body) => client.post(`/surveys/${surveyId}/approve`, body).then(r => r.data),
    getApprovedSpecification: (projectId) => client.get(`/projects/${projectId}/approved-specification`).then(r => r.data),
    // DEV-010B-1 / DEV-010B-2 · Engineer-scoped survey list (Mobile + Web).
    listMySurveys: () => client.get(`/engineers/me/surveys`).then(r => r.data),
    // DEV-010D · Derived engineer work queue (tasks + returned surveys).
    myOutstandingWork: () => client.get(`/engineers/me/outstanding-work`).then(r => r.data),
    // Upload a document to a project (existing endpoint) — used by
    // survey-photo attach flow on Web. Sends multipart/form-data.
    uploadProjectDocument: (projectId, file, { title, category = "Site Survey", visibility = "internal" } = {}) => {
        const form = new FormData();
        form.append("file", file);
        if (title) form.append("title", title);
        form.append("category", category);
        form.append("visibility", visibility);
        return client.post(`/projects/${projectId}/documents/upload`, form, {
            headers: { "Content-Type": "multipart/form-data" },
        }).then(r => r.data);
    },

    // Calendar module
    listCalendarAppointments: (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") qs.set(k, v);
        });
        return client.get(`/calendar/appointments?${qs.toString()}`).then(r => r.data);
    },
    getCalendarAppointment: (id) => client.get(`/calendar/appointments/${id}`).then(r => r.data),
    createCalendarAppointment: (body) => client.post("/calendar/appointments", body).then(r => r.data),
    updateCalendarAppointment: (id, body) => client.patch(`/calendar/appointments/${id}`, body).then(r => r.data),
    // VISIT-TASK-001 · Read-only list of appointment↔task links so the
    // Calendar detail panel can render required-task status + navigate
    // to the existing authorised task surface.
    calendarAppointmentTasks: (id) =>
        client.get(`/calendar/appointments/${id}/tasks`).then(r => r.data),
    // CAL-001 · Bank holidays + blackout admin.
    getBankHolidays: () => client.get("/calendar/holidays").then(r => r.data),
    listBlackouts: (fromIso, toIso) =>
        client.get("/calendar/blackouts", { params: { from: fromIso, to: toIso } }).then(r => r.data),
    getOrganisationSettings: () => client.get("/organisation/settings").then(r => r.data),
    patchOrganisationSettings: (body) => client.patch("/organisation/settings", body).then(r => r.data),
    adminListBlackouts: () => client.get("/admin/blackouts").then(r => r.data),
    adminCreateBlackout: (body) => client.post("/admin/blackouts", body).then(r => r.data),
    adminUpdateBlackout: (id, body) => client.patch(`/admin/blackouts/${id}`, body).then(r => r.data),
    adminDeleteBlackout: (id) => client.delete(`/admin/blackouts/${id}`).then(r => r.data),
    adminRefreshBankHolidays: () => client.post("/admin/blackouts/refresh-holidays").then(r => r.data),
    cancelCalendarAppointment: (id, reason) => client.post(`/calendar/appointments/${id}/cancel`, { reason }).then(r => r.data),
    completeCalendarAppointment: (id) => client.post(`/calendar/appointments/${id}/complete`).then(r => r.data),
    checkCalendarClash: (body) => client.post("/calendar/check-clash", body).then(r => r.data),
    // OPS-006A · consolidated pre-flight (clash + non-working-day + holiday)
    checkCalendarAvailability: (body) =>
        client.post("/calendar/check-availability", body).then(r => r.data),
    listAppointmentTypes: () => client.get("/calendar/appointment-types").then(r => r.data.types),

    // Notifications
    listNotifications: (status = "all") => client.get(`/notifications?status=${status}`).then(r => r.data),
    unreadCount: () => client.get("/notifications/unread-count").then(r => r.data.count),
    markNotificationRead: (id) => client.post(`/notifications/${id}/read`).then(r => r.data),
    markAllNotificationsRead: () => client.post("/notifications/read-all").then(r => r.data),
    archiveNotification: (id) => client.post(`/notifications/${id}/archive`).then(r => r.data),
    getNotificationPreferences: () => client.get("/notifications/preferences").then(r => r.data),
    updateNotificationPreferences: (body) => client.put("/notifications/preferences", body).then(r => r.data),
    adminNotificationEvents: () => client.get("/notifications/admin/events").then(r => r.data),
    adminNotificationTemplates: () => client.get("/notifications/admin/templates").then(r => r.data),
    adminNotificationHistory: (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => { if (v) qs.set(k, v); });
        return client.get(`/notifications/admin/history?${qs.toString()}`).then(r => r.data);
    },
    adminNotificationSettings: () => client.get("/notifications/admin/settings").then(r => r.data),
    adminNotificationRetry: (id) => client.post(`/notifications/admin/${id}/retry`).then(r => r.data),
    adminNotificationRetryAll: () => client.post("/notifications/admin/retry-all").then(r => r.data),
    adminTestFire: (body) => client.post("/notifications/admin/test-fire", body).then(r => r.data),
    adminNotificationWidgets: () => client.get("/notifications/admin/dashboard-widgets").then(r => r.data),

    // Task Management
    listTasks: (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") qs.set(k, v);
        });
        return client.get(`/tasks?${qs.toString()}`).then(r => r.data);
    },
    getTask: (id) => client.get(`/tasks/${id}`).then(r => r.data),
    createTask: (body) => client.post("/tasks", body).then(r => r.data),
    updateTask: (id, body) => client.patch(`/tasks/${id}`, body).then(r => r.data),
    completeTask: (id) => client.post(`/tasks/${id}/complete`).then(r => r.data),
    cancelTask: (id, reason) => client.post(`/tasks/${id}/cancel`, { reason }).then(r => r.data),
    bulkTaskAction: (body) => client.post("/tasks/bulk", body).then(r => r.data),
    listTaskTemplates: () => client.get("/tasks/templates").then(r => r.data),
    getTaskTemplate: (id) => client.get(`/tasks/templates/${id}`).then(r => r.data),
    createFromTemplate: (body) => client.post("/tasks/from-template", body).then(r => r.data),
    projectTasks: (projectId) => client.get(`/projects/${projectId}/tasks`).then(r => r.data),
    taskMetadata: () => client.get("/tasks/metadata").then(r => r.data),
    taskDashboardMetrics: () => client.get("/tasks/dashboard-metrics").then(r => r.data),

    // FIELD-001C Section B · Attributed notes + photo evidence.
    listTaskEntries: (taskRef) =>
        client.get(`/task-entries?ref=${encodeURIComponent(taskRef)}`).then(r => r.data),
    createTaskEntry: ({ taskRef, body = "", photos = [], idempotencyKey }) => {
        const fd = new FormData();
        fd.append("ref", taskRef);
        fd.append("body", body || "");
        (photos || []).forEach((f) => fd.append("photos", f, f.name));
        return client.post("/task-entries", fd, {
            headers: {
                "Content-Type": "multipart/form-data",
                ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
            },
        }).then(r => r.data);
    },
    taskEntryPhotoToken: (entryId, photoId) =>
        client.get(`/task-entries/${entryId}/photos/${photoId}/download-token`).then(r => r.data),

    myWorkload: () => client.get("/engineers/me/workload").then(r => r.data),

    listEngineers: ({ includeArchived = false, q = "" } = {}) => {
        const params = new URLSearchParams();
        if (includeArchived) params.set("include_archived", "true");
        if (q) params.set("q", q);
        const qs = params.toString();
        return client.get(`/engineers${qs ? `?${qs}` : ""}`).then(r => r.data);
    },
    engineerPalette: () => client.get("/engineers/palette").then(r => r.data.palette),
    engineerWorkloadSummary: () => client.get("/engineers/workload-summary").then(r => r.data),
    createEngineer: (body) => client.post("/engineers", body).then(r => r.data),
    updateEngineer: (id, body) => client.patch(`/engineers/${id}`, body).then(r => r.data),
    archiveEngineer: (id) => client.post(`/engineers/${id}/archive`).then(r => r.data),
    reactivateEngineer: (id) => client.post(`/engineers/${id}/reactivate`).then(r => r.data),
    engineerAvailability: (id) => client.get(`/engineers/${id}/availability`).then(r => r.data),
    uploadEngineerPhoto: (id, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return client.post(`/engineers/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
    },
    deleteEngineerPhoto: (id) => client.delete(`/engineers/${id}/photo`).then(r => r.data),
    assignLeadEngineer: (projectId, leadEngineerId) =>
        client.patch(`/projects/${projectId}/lead-engineer`, { lead_engineer_id: leadEngineerId }).then(r => r.data),
    // FIELD-001B / FIELD-001C2A · Operations-only canonical task assignment.
    // Optional `requiredResourceType`:
    //   • undefined → leave any existing requirement untouched.
    //   • ""        → clear the requirement.
    //   • non-empty → set (server rejects mismatched assignees with 409).
    updateTaskAssignees: (projectId, taskId, assigneeIds, opts = {}) => {
        const payload = { assignee_ids: assigneeIds || [] };
        if (opts.requiredResourceType !== undefined) {
            payload.required_resource_type = opts.requiredResourceType || null;
        }
        return client.patch(
            `/projects/${projectId}/tasks/${taskId}/assignees`, payload,
        ).then(r => r.data);
    },
    // FIELD-001C · Project-scoped assignment options (Ops or Lead).
    // Same safe field set as the task-scoped variant; used by
    // New-task forms where a task id doesn't yet exist.
    projectAssignmentOptions: (projectId) =>
        client.get(`/projects/${projectId}/assignment-options`).then(r => r.data),
    // FIELD-001C · Server-authoritative list of projects the caller may
    // create tasks on. Ops → every project; Field Project Lead → only
    // projects where `lead_engineer_id` resolves to their engineer id;
    // ordinary Field User → []. Belt-and-braces to the /tasks POST
    // authority — do not trust a client-supplied project list.
    taskCreationOptions: () =>
        client.get(`/projects/task-creation-options`).then(r => r.data),
    // FIELD-001C · Canonical task self-assign. Mirrors ad-hoc.
    assignCanonicalTaskToMe: (projectId, taskId) =>
        client.post(`/projects/${projectId}/tasks/${taskId}/assign-to-me`).then(r => r.data),
    // FIELD-001C · Minimal Lead-scoped assignment options for a
    // canonical task. Ops sees the same list; non-Ops non-Lead → 403.
    canonicalAssignmentOptions: (projectId, taskId) =>
        client.get(`/projects/${projectId}/tasks/${taskId}/assignment-options`).then(r => r.data),
    // FIELD-001C2A · Ops-only canonical task reopen. Requires a non-empty reason.
    reopenPhaseTask: (projectId, taskId, reason) =>
        client.post(`/projects/${projectId}/tasks/${taskId}/reopen`,
                    { reason }).then(r => r.data),
    // FIELD-001C2B · Ops-only ad-hoc task reopen. Same reason contract.
    reopenAdHocTask: (taskId, reason) =>
        client.post(`/tasks/${taskId}/reopen`, { reason }).then(r => r.data),
    // FIELD-001C2B · Explicit self-assign. Server enforces eligibility
    // + atomic still-unassigned/incomplete guard.
    assignTaskToMe: (taskId) =>
        client.post(`/tasks/${taskId}/assign-to-me`).then(r => r.data),
    projectTeam: (projectId) =>
        client.get(`/projects/${projectId}/team`).then(r => r.data),
    listAudit: (projectId) => client.get(`/projects/${projectId}/audit`).then(r => r.data),

    // OPS-001 · Operations Reporting & Control Hub
    operationsReportsSummary: (period = "30d") =>
        client.get(`/operations/reports/summary?period=${encodeURIComponent(period)}`).then(r => r.data),

    // OPS-004B · Canonical Operations dashboard aggregate.
    operationsDashboardSummary: () =>
        client.get(`/operations/dashboard/summary`).then(r => r.data),

    // OPS-004B1 · Canonical stage catalogue (backend SSOT).
    operationsStageCatalogue: () =>
        client.get(`/operations/stages/catalogue`).then(r => r.data),

    // SALES-ROLE-001B · Sales-scoped workspace aggregate.
    salesDashboardSummary: () =>
        client.get(`/sales/dashboard/summary`).then(r => r.data),

    // OPS-002 · Built-in queue counts + compound filter query + saved views.
    operationsQueues: () =>
        client.get(`/operations/queues`).then(r => r.data),
    operationsProjectsQuery: (filters = {}) => {
        const p = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v == null || v === "" || v === false) return;
            p.set(k, String(v));
        });
        const qs = p.toString();
        return client.get(`/operations/projects/query${qs ? "?" + qs : ""}`).then(r => r.data);
    },
    operationsSavedViewsList: () =>
        client.get(`/operations/saved-views`).then(r => r.data),
    operationsSavedViewCreate: (name, filters) =>
        client.post(`/operations/saved-views`, { name, filters }).then(r => r.data),
    operationsSavedViewUpdate: (id, patch) =>
        client.patch(`/operations/saved-views/${encodeURIComponent(id)}`, patch).then(r => r.data),
    operationsSavedViewDelete: (id) =>
        client.delete(`/operations/saved-views/${encodeURIComponent(id)}`).then(r => r.data),

    // OPS-003 · Operations Attention Centre (DEV-016 exceptions surface)
    operationsExceptionsSearch: (filters = {}) => {
        const p = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
            if (v == null || v === "" || v === false) return;
            p.set(k, String(v));
        });
        return client.get(`/operations/rules/exceptions/search?${p.toString()}`).then(r => r.data);
    },
    operationsExceptionsSummary: () =>
        client.get(`/operations/rules/exceptions/summary`).then(r => r.data),
    operationsExceptionDetail: (id) =>
        client.get(`/operations/rules/exceptions/${encodeURIComponent(id)}`).then(r => r.data),
    operationsRulesRegistry: () =>
        client.get(`/operations/rules/registry`).then(r => r.data),

    // OPS-003B · Operations Attention Centre operational actions.
    // Every mutation is Operations-only; server derives actor
    // identity (client never supplies actor ids).
    operationsAttentionAssignees: () =>
        client.get(`/operations/attention/assignees`).then(r => r.data),
    operationsAttentionAssign: (exceptionId, body) =>
        client.post(`/operations/attention/exceptions/${encodeURIComponent(exceptionId)}/assign`, body).then(r => r.data),
    operationsAttentionAcknowledge: (exceptionId, body = {}) =>
        client.post(`/operations/attention/exceptions/${encodeURIComponent(exceptionId)}/acknowledge`, body).then(r => r.data),
    operationsAttentionAddNote: (exceptionId, content) =>
        client.post(`/operations/attention/exceptions/${encodeURIComponent(exceptionId)}/notes`, { content }).then(r => r.data),
    operationsAttentionReevaluate: (exceptionId) =>
        client.post(`/operations/attention/exceptions/${encodeURIComponent(exceptionId)}/reevaluate`).then(r => r.data),

    // NOT-001C · Operations Notification Centre
    opsNotifSummary: (params = {}) => {
        const p = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v == null || v === "") return;
            p.set(k, String(v));
        });
        const q = p.toString();
        return client.get(`/notifications/foundation/summary${q ? `?${q}` : ""}`).then(r => r.data);
    },
    opsNotifSettings: () =>
        client.get(`/notifications/foundation/settings`).then(r => r.data),
    opsNotifFilters: () =>
        client.get(`/notifications/foundation/filters`).then(r => r.data),
    opsNotifEvents: (params = {}) => {
        const p = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v == null || v === "" || v === false) return;
            p.set(k, String(v));
        });
        return client.get(`/notifications/foundation/events?${p.toString()}`).then(r => r.data);
    },
    opsNotifEvent: (id) =>
        client.get(`/notifications/foundation/events/${encodeURIComponent(id)}`).then(r => r.data),
    opsNotifEventTimeline: (id) =>
        client.get(`/notifications/foundation/events/${encodeURIComponent(id)}/timeline`).then(r => r.data),
    opsNotifDeliveries: (params = {}) => {
        const p = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v == null || v === "" || v === false) return;
            p.set(k, String(v));
        });
        return client.get(`/notifications/foundation/deliveries?${p.toString()}`).then(r => r.data);
    },
    opsNotifDelivery: (id) =>
        client.get(`/notifications/foundation/deliveries/${encodeURIComponent(id)}`).then(r => r.data),
    opsNotifRetry: (id, { reason, idempotencyKey } = {}) =>
        client.post(
            `/notifications/foundation/deliveries/${encodeURIComponent(id)}/retry`,
            { reason: reason || null },
            { headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {} },
        ).then(r => r.data),
    opsNotifResend: (id, { reason, idempotencyKey }) =>
        client.post(
            `/notifications/foundation/deliveries/${encodeURIComponent(id)}/resend`,
            { reason },
            { headers: { "Idempotency-Key": idempotencyKey } },
        ).then(r => r.data),

    // NOT-001C2 · per-user triage
    opsNotifTriageMarkRead: (subject_type, ids) =>
        client.post(`/notifications/foundation/triage/mark_read`,
            { subject_type, ids }).then(r => r.data),
    opsNotifTriageMarkUnread: (subject_type, ids) =>
        client.post(`/notifications/foundation/triage/mark_unread`,
            { subject_type, ids }).then(r => r.data),
    opsNotifTriageArchive: (subject_type, ids) =>
        client.post(`/notifications/foundation/triage/archive`,
            { subject_type, ids }).then(r => r.data),
    opsNotifTriageUnarchive: (subject_type, ids) =>
        client.post(`/notifications/foundation/triage/unarchive`,
            { subject_type, ids }).then(r => r.data),
    // FIELD-IDENTITY-001A · Operations-only identity audit dry-run.
    identityAuditDryRun: () =>
        client.post(`/operations/identity/reconciliation/dry-run`).then(r => r.data),
    // RESOURCE-USER-LINK-001 · Narrow user directory for the
    // Resource Management "add from existing user" picker. Guarded
    // on the backend by ``manage_engineers`` — a resource manager
    // never needs ``manage_users`` to populate this picker.
    engineerUserLookup: ({ q = "", includeLinked = true } = {}) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (!includeLinked) params.set("include_linked", "false");
        const qs = params.toString();
        return client.get(`/engineers/user-lookup${qs ? `?${qs}` : ""}`).then(r => r.data);
    },
    // RESOURCE-USER-LINK-001 · Staff-role user list for the Field
    // Team linkedUserId edit-time picker. Server owns the role
    // filter — we still defensively filter client-side to installer
    // + sales + operations (never customer). Sales users must
    // appear here so their linked resource can be relinked.
    listInstallerUsers: (q = "") =>
        client.get(`/admin/security/users${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => {
            const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
            const eligible = new Set(["installer", "sales", "operations"]);
            return rows.filter((u) => eligible.has((u.role || "").toLowerCase()));
        }),
};

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    // OPS-004B3 · Structured completion-prereq refusal:
    //   {code:"COMPLETION_PREREQUISITES_UNMET", message, unmet:[{key,message,...}]}
    if (detail && detail.code === "COMPLETION_PREREQUISITES_UNMET"
        && Array.isArray(detail.unmet) && detail.unmet.length > 0) {
        const items = detail.unmet.map((u) => `• ${u.message || u.key}`).join("\n");
        return `${detail.message || "Cannot complete project — unmet prerequisites."}\n${items}`;
    }
    if (detail && typeof detail.message === "string") return detail.message;
    if (detail && typeof detail.msg === "string") return detail.msg;
    // VISIT-TASK-001B · MappingError / LinkageError envelopes use
    // ``{code, detail, ...extra}`` where ``detail`` carries the
    // human-readable string. Previously fell through to
    // ``String(detail)`` which produced "[object Object]".
    if (detail && typeof detail.detail === "string") {
        // Include the code in brackets so operators can grep the
        // audit / support conversations, but keep the primary
        // wording user-friendly.
        return detail.code
            ? `${detail.detail} (${detail.code})`
            : detail.detail;
    }
    // Last-resort defensive fallback — never leak "[object Object]".
    try {
        const j = JSON.stringify(detail);
        if (j && j !== "{}") return j;
    } catch { /* ignore */ }
    return "Something went wrong. Please try again.";
}
